import React, { useState, useMemo, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';

const ApprovalLimitsTable = ({ data, totalRecords, isLoading, onPage, lazyParams, onEdit, onHistory, resetPagination = false }) => {
    const { th } = useLanguage();
    const safeParams = lazyParams || { page: 1, rows: 10 };

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [currentFirst, setCurrentFirst] = useState(0);

    useEffect(() => {
        if (resetPagination) {
            setCurrentFirst(0);
            setSortField(null);
            setSortOrder(null);
        }
    }, [resetPagination]);

    const handlePage = (event) => {
        setCurrentFirst(event.first);
        onPage(event);
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const sortedData = useMemo(() => {
        if (!sortField || !data) return data;

        return [...data].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortOrder === 1 ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }

            if (aVal < bVal) return sortOrder === 1 ? -1 : 1;
            if (aVal > bVal) return sortOrder === 1 ? 1 : -1;
            return 0;
        });
    }, [data, sortField, sortOrder]);

    const actionTemplate = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <i onClick={() => onEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')}></i>
                <i onClick={() => onHistory(rowData)} className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.history')}></i>
            </div>
        );
    };

    const priceTemplate = (rowData, field) => {
        const value = rowData[field];
        return value !== null && value !== undefined ? `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(value)} ${rowData.currency}` : '-';
    };

    const statusTemplate = (rowData) => {
        return <span className={`p-tag ${rowData.isActive ? 'p-tag-success' : 'p-tag-danger'}`}>{rowData.isActive ? th('common.active') : th('common.inactive')}</span>;
    };

    return (
        <DataTable
            value={sortedData}
            paginator
            lazy
            first={currentFirst}
            rows={safeParams.rows}
            totalRecords={totalRecords}
            loading={isLoading}
            onPage={handlePage}
            onSort={onSort}
            sortField={sortField}
            sortOrder={sortOrder}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            dataKey="id"
            tableStyle={{ minWidth: '80rem' }}
            showGridlines
            stripedRows
            emptyMessage={th('approvalLimits.emptyMessage')}
            removableSort
            sortMode="single"
        >
            <Column body={actionTemplate} style={{ width: '8%' }} align="center" />
            <Column field="startDate" header={th('common.startDate')} body={(row) => formatDateOnly(row?.startDate)} sortable style={{ width: '130px' }} />
            <Column field="finishDate" header={th('common.endDate')} body={(row) => formatDateOnly(row?.finishDate)} sortable style={{ width: '130px' }} />
            <Column field="startingPrice" header={th('approvalLimits.startingPrice')} body={(rowData) => priceTemplate(rowData, 'startingPrice')} sortable style={{ width: '140px' }} align="right" />
            <Column field="endingPrice" header={th('approvalLimits.endingPrice')} body={(rowData) => priceTemplate(rowData, 'endingPrice')} sortable style={{ width: '140px' }} align="right" />
            <Column field="currency" header={th('labor.currency')} sortable style={{ width: '100px' }} align="center" />
            <Column field="approvalGroupName" header={th('approvalGroups.title')} sortable style={{ minWidth: '150px' }} />
            <Column field="createdName" header={th('common.created')} sortable style={{ width: '120px' }} align="center" />
            <Column field="createdDate" header={th('common.createdDate')} body={(row) => formatDateOnly(row?.createdDate)} sortable style={{ width: '140px' }} />
            <Column field="modifiedName" header={th('common.updatedBy')} sortable style={{ minWidth: '200px' }} />
            <Column field="modifiedDate" header={th('common.updatedDate')} body={(row) => formatDateOnly(row?.modifiedDate)} sortable style={{ width: '200px' }} />
        </DataTable>
    );
};

export default ApprovalLimitsTable;
